/**
 * The single lane every reconcile pass runs in.
 *
 * Multi-device sync and Google Calendar sync are independent stores with
 * independent `busy` guards, but they are both debounced ~2.5s off the SAME task
 * store, so a local edit wakes both at once — and they then interleave at every
 * `await` over the same repositories. That cost real data:
 *
 *  - Google sync adopted a new event's `external_id` while the server sync was
 *    mid-flight with a pre-adoption snapshot; the server's echo landed after and
 *    reset the link, so the next push created a SECOND Google event and the next
 *    pull re-imported the orphan as a duplicate task.
 *  - The server sync's `applySyncGraph` overwrote a task the Google pull had just
 *    rewritten, and vice versa, with neither side seeing the other's write.
 *
 * Field-level ownership (`TASK_REMOTE_LINK_FIELDS`) makes those specific losses
 * impossible; this makes the whole class impossible. Passes queue FIFO and run
 * one at a time. Ordering costs latency, never correctness — which is the trade
 * this system wants.
 */

let tail: Promise<unknown> = Promise.resolve();
let depth = 0;

/** True while a reconcile pass holds or is queued for the lane. */
export function syncLaneBusy(): boolean {
  return depth > 0;
}

/**
 * Run `fn` with exclusive access to local storage against other reconcile
 * passes. Rejections propagate to the caller but never poison the lane — the
 * next queued pass runs regardless.
 */
export function withSyncLane<T>(fn: () => Promise<T>): Promise<T> {
  depth++;
  const run = tail.then(fn, fn);
  // Swallow on the CHAIN only: the returned promise still rejects for the
  // caller, but a failed pass must not reject every pass queued behind it.
  tail = run.then(() => undefined, () => undefined);
  return run.finally(() => { depth--; });
}

/**
 * Lane acquisition for callers whose pass body is too long to wrap in a closure
 * without reindenting it. Await the returned release and call it in a `finally`
 * — skipping it wedges every later pass.
 */
export function acquireSyncLane(): Promise<() => void> {
  depth++;
  let release!: () => void;
  const held = new Promise<void>((resolve) => {
    release = () => { depth--; resolve(); };
  });
  const turn = tail.then(() => undefined, () => undefined);
  tail = turn.then(() => held);
  return turn.then(() => release);
}
